import React, { useState } from 'react';
import { useT } from '../i18n';

export interface OaiTimelineData {
  months_consumed_elsewhere: number;
  phase_transition_date: string | null;   // null = pas encore basculé en ET
  phase_planned: boolean;                  // true = date future planifiée, false = passé réel
  contracts: Array<{ id: number; start: string; end: string | null; label?: string }>;
  next_measure_type: string | null;
  has_external_placement: boolean;
}

interface Props {
  measureStartDate: string;
  measureEndDate: string | null;
  timeline: OaiTimelineData;
  currentTaux: number | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

const BUDGET = 12;  // mois OAI max
const W = 1000;     // SVG internal width

function toMs(iso: string): number {
  return new Date(iso.slice(0, 10) + 'T00:00:00Z').getTime();
}

/** Converts an absolute date to months within the 12-month OAI budget. */
function dateToTotalMonths(date: string, startDate: string, consumed: number): number {
  return consumed + (toMs(date) - toMs(startDate)) / (1000 * 60 * 60 * 24 * 30.44);
}

/** Maps a budget month to SVG X coordinate. */
function mx(months: number): number {
  return Math.max(0, Math.min(W, (months / BUDGET) * W));
}

type Status = 'before-eveil' | 'past-ok' | 'past-warn' | 'approaching' | 'future';

const STATUS_FILL: Record<Status, string> = {
  'before-eveil': 'var(--ink-3)',
  'past-ok':      'var(--ok)',
  'past-warn':    'var(--err)',
  'approaching':  'var(--warn)',
  'future':       'var(--surface)',
};
const STATUS_STROKE: Record<Status, string> = {
  'before-eveil': 'var(--ink-3)',
  'past-ok':      'var(--ok)',
  'past-warn':    'var(--err)',
  'approaching':  'var(--warn)',
  'future':       'var(--ink-3)',
};

// ── Milestone definitions ─────────────────────────────────────────────────────

interface Milestone {
  month: number;
  topLabel: string;
  botLabel: string;
  check: (taux: number | null, hasPhase: boolean, hasPlacement: boolean) => boolean;
}

/** Milestone label definitions. `t` is passed in from the component (plain helper, not a hook). */
function getMilestones(t: (key: string) => string): Milestone[] {
  return [
    {
      month: 3,
      topLabel: t('timeline.milestone.taux30'),
      botLabel: t('timeline.milestone.tauxMin'),
      check: (taux) => taux != null && taux >= 30,
    },
    {
      month: 6,
      topLabel: t('timeline.milestone.taux50'),
      botLabel: t('timeline.milestone.tauxMin'),
      check: (taux) => taux != null && taux >= 50,
    },
    {
      month: 9,
      topLabel: t('timeline.milestone.etActif'),
      botLabel: t('timeline.milestone.auPlusTard'),
      check: (_t, hasPhase) => hasPhase,
    },
    {
      month: 12,
      topLabel: t('timeline.milestone.stageExt'),
      botLabel: t('timeline.milestone.requis'),
      check: (_t, _p, hasPlacement) => hasPlacement,
    },
  ];
}

// ── Component ─────────────────────────────────────────────────────────────────

export function MeasureTimeline({ measureStartDate, measureEndDate, timeline, currentTaux }: Props) {
  const { t } = useT();
  const { months_consumed_elsewhere: consumed, phase_transition_date, phase_planned, contracts, next_measure_type, has_external_placement } = timeline;
  const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
  const MILESTONES = getMilestones(t);

  const today = new Date().toISOString().slice(0, 10);
  const todayMonths = dateToTotalMonths(today, measureStartDate, consumed);
  const todayX = mx(todayMonths);
  const consumedX = mx(consumed);

  const phaseMonths = phase_transition_date
    ? dateToTotalMonths(phase_transition_date, measureStartDate, consumed)
    : null;
  const phaseX = phaseMonths != null ? mx(phaseMonths) : null;

  const hasPhase = phase_transition_date != null && !phase_planned;

  // Budget counter
  const budgetMonthNow = Math.min(BUDGET, Math.max(0, todayMonths));
  const budgetRounded = Math.floor(budgetMonthNow * 10) / 10;

  // Milestone status
  function getStatus(m: Milestone): Status {
    if (m.month <= consumed) return 'before-eveil';
    const daysUntil = (m.month - todayMonths) * 30.44;
    if (todayMonths >= m.month) {
      return m.check(currentTaux, hasPhase, has_external_placement) ? 'past-ok' : 'past-warn';
    }
    if (daysUntil <= 45) return 'approaching';
    return 'future';
  }

  // SVG layout constants
  const TRACK_Y = 54;
  const TRACK_H = 16;
  const MIL_CY = 30;   // milestone circle center Y
  const MIL_R = 11;    // milestone circle radius
  const SVG_H = 110;

  return (
    <div style={{ width: '100%' }}>
      {/* Header row */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 'var(--s8)', gap: 'var(--s8)', flexWrap: 'wrap',
      }}>
        <span style={{ fontSize: 'var(--t-12)', fontWeight: 600, color: 'var(--ink-2)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {t('timeline.measure.title')}
        </span>
        <div style={{ display: 'flex', gap: 'var(--s8)', alignItems: 'center', flexWrap: 'wrap' }}>
          {consumed > 0 && (
            <span style={{ fontSize: 'var(--t-11)', color: 'var(--ink-3)' }}>
              {t('timeline.measure.priorMonths', { n: consumed })}
            </span>
          )}
          <span style={{
            padding: '2px 10px',
            borderRadius: 'var(--r2)',
            fontWeight: 700,
            fontSize: 'var(--t-11)',
            background: budgetMonthNow >= 10
              ? 'color-mix(in srgb, var(--err) 12%, var(--surface))'
              : budgetMonthNow >= 8
                ? 'color-mix(in srgb, var(--warn) 14%, var(--surface))'
                : 'color-mix(in srgb, var(--accent) 12%, var(--surface))',
            color: budgetMonthNow >= 10
              ? 'var(--err)'
              : budgetMonthNow >= 8
                ? 'var(--warn)'
                : 'var(--accent)',
          }}>
            {t('timeline.measure.budgetLabel', { month: budgetRounded, budget: BUDGET })}
          </span>
        </div>
      </div>

      {/* SVG Timeline */}
      <svg
        viewBox={`0 0 ${W} ${SVG_H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        onMouseLeave={() => setTooltip(null)}
      >
        {/* ── Background track ── */}
        <rect
          x={0} y={TRACK_Y} width={W} height={TRACK_H} rx={TRACK_H / 2}
          fill="color-mix(in srgb, var(--line) 80%, var(--bg))"
        />

        {/* ── Consumed elsewhere (grayed zone) ── */}
        {consumed > 0 && (
          <rect
            x={0} y={TRACK_Y} width={consumedX} height={TRACK_H} rx={TRACK_H / 2}
            fill="var(--ink-3)" opacity={0.3}
          />
        )}

        {/* ── EP segment (progressive) ── */}
        {phaseX != null ? (
          <rect
            x={Math.max(0, consumedX)}
            y={TRACK_Y}
            width={Math.max(0, Math.min(phaseX, todayX) - Math.max(0, consumedX))}
            height={TRACK_H}
            fill="color-mix(in srgb, var(--accent) 50%, var(--bg))"
          />
        ) : (
          <rect
            x={Math.max(0, consumedX)}
            y={TRACK_Y}
            width={Math.max(0, todayX - Math.max(0, consumedX))}
            height={TRACK_H}
            fill="color-mix(in srgb, var(--accent) 50%, var(--bg))"
          />
        )}

        {/* ── ET segment (travail) ── */}
        {phaseX != null && todayX > phaseX && !phase_planned && (
          <rect
            x={phaseX}
            y={TRACK_Y}
            width={todayX - phaseX}
            height={TRACK_H}
            fill="color-mix(in srgb, var(--ok) 55%, var(--bg))"
          />
        )}

        {/* ── Contract outlines ── */}
        {contracts.map((c, i) => {
          const cx1 = mx(dateToTotalMonths(c.start, measureStartDate, consumed));
          const cx2 = c.end
            ? mx(dateToTotalMonths(c.end, measureStartDate, consumed))
            : Math.min(W, todayX + 2);
          const cw = cx2 - cx1;
          if (cw <= 0) return null;
          return (
            <rect
              key={c.id}
              x={cx1} y={TRACK_Y} width={cw} height={TRACK_H}
              rx={i === 0 ? TRACK_H / 2 : 2}
              fill="none"
              stroke={i === 0 ? 'var(--accent)' : 'var(--ok)'}
              strokeWidth={1.5}
              strokeDasharray={i === 0 ? 'none' : '5 3'}
              onMouseEnter={(e) => setTooltip({ x: cx1 + cw / 2, y: TRACK_Y - 6, text: c.label || t('timeline.contract.label', { n: i + 1 }) })}
            />
          );
        })}

        {/* ── Phase transition line ── */}
        {phaseX != null && phaseX > consumedX && (
          <g>
            <line
              x1={phaseX} y1={TRACK_Y - 10} x2={phaseX} y2={TRACK_Y + TRACK_H + 6}
              stroke={phase_planned ? 'var(--ink-3)' : 'var(--ok)'}
              strokeWidth={2}
              strokeDasharray={phase_planned ? '5 3' : '4 2'}
            />
            <text
              x={phaseX + 5} y={TRACK_Y - 12}
              fontSize={9} fontWeight={600}
              fill={phase_planned ? 'var(--ink-3)' : 'var(--ok)'}
            >
              {t('timeline.phase.transition')}{phase_planned ? ` ${t('timeline.phase.planned')}` : ''}
            </text>
          </g>
        )}

        {/* ── Today indicator ── */}
        {todayMonths >= 0 && todayMonths <= BUDGET && (
          <g>
            <line
              x1={todayX} y1={TRACK_Y - 6} x2={todayX} y2={TRACK_Y + TRACK_H + 6}
              stroke="var(--accent)" strokeWidth={2}
            />
            <polygon
              points={`${todayX - 4},${TRACK_Y - 6} ${todayX + 4},${TRACK_Y - 6} ${todayX},${TRACK_Y - 1}`}
              fill="var(--accent)"
            />
          </g>
        )}

        {/* ── Month ticks + labels (M0, M3, M6, M9, M12) ── */}
        {[0, 3, 6, 9, 12].map(m => {
          const x = mx(m);
          return (
            <g key={m}>
              <line
                x1={x} y1={TRACK_Y + TRACK_H} x2={x} y2={TRACK_Y + TRACK_H + 6}
                stroke="var(--ink-3)" strokeWidth={1}
              />
              <text
                x={x} y={TRACK_Y + TRACK_H + 16}
                textAnchor="middle" fontSize={10}
                fill={m === 12 ? 'var(--ink-2)' : 'var(--ink-3)'}
                fontWeight={m === 0 || m === 12 ? 600 : 400}
              >
                M{m}
              </text>
            </g>
          );
        })}

        {/* ── Milestone markers ── */}
        {MILESTONES.map(ms => {
          const x = mx(ms.month);
          const status = getStatus(ms);
          const fill = STATUS_FILL[status];
          const stroke = STATUS_STROKE[status];
          const isPastOk = status === 'past-ok';
          const isPastWarn = status === 'past-warn';
          const isApproaching = status === 'approaching';
          const isBeforeEveil = status === 'before-eveil';

          return (
            <g
              key={ms.month}
              onMouseEnter={() => {
                const texts: Record<Status, string> = {
                  'before-eveil': t('timeline.milestone.tooltipBeforeEveil'),
                  'past-ok':      t('timeline.milestone.tooltipPastOk'),
                  'past-warn':    t('timeline.milestone.tooltipPastWarn'),
                  'approaching':  t('timeline.milestone.tooltipApproaching'),
                  'future':       t('timeline.milestone.tooltipFuture'),
                };
                setTooltip({ x, y: MIL_CY - MIL_R - 4, text: texts[status] });
              }}
              onMouseLeave={() => setTooltip(null)}
              style={{ cursor: 'default' }}
            >
              {/* Connector */}
              <line
                x1={x} y1={MIL_CY + MIL_R} x2={x} y2={TRACK_Y}
                stroke={stroke} strokeWidth={1}
                strokeDasharray={status === 'future' ? '3 2' : 'none'}
                opacity={isBeforeEveil ? 0.4 : 1}
              />

              {/* Circle */}
              <circle
                cx={x} cy={MIL_CY} r={MIL_R}
                fill={fill}
                stroke={stroke}
                strokeWidth={isApproaching ? 2.5 : 1.5}
                opacity={isBeforeEveil ? 0.45 : 1}
              />

              {/* Icon inside circle */}
              {isPastOk && (
                <text x={x} y={MIL_CY + 4} textAnchor="middle" fontSize={11} fill="white" fontWeight={700}>✓</text>
              )}
              {isPastWarn && (
                <text x={x} y={MIL_CY + 4} textAnchor="middle" fontSize={11} fill="white" fontWeight={700}>!</text>
              )}
              {isApproaching && (
                <text x={x} y={MIL_CY + 4} textAnchor="middle" fontSize={9} fill="white" fontWeight={700}>!</text>
              )}
              {(status === 'future' || isBeforeEveil) && (
                <text x={x} y={MIL_CY + 3} textAnchor="middle" fontSize={8} fill={stroke} fontWeight={500}>
                  {ms.month}
                </text>
              )}

              {/* Top label (requirement) */}
              <text
                x={x} y={6} textAnchor="middle" fontSize={9}
                fill={stroke} fontWeight={isApproaching || isPastWarn ? 700 : 500}
                opacity={isBeforeEveil ? 0.45 : 1}
              >
                {ms.topLabel}
              </text>

              {/* Bottom sub-label */}
              <text
                x={x} y={TRACK_Y + TRACK_H + 30} textAnchor="middle" fontSize={8}
                fill={stroke} opacity={isBeforeEveil ? 0.35 : 0.75}
              >
                {ms.botLabel}
              </text>
            </g>
          );
        })}

        {/* ── "Après M12" label ── */}
        {next_measure_type && todayMonths > 11 && (
          <text x={W - 4} y={TRACK_Y + TRACK_H / 2 + 4} textAnchor="end" fontSize={10} fill="var(--ink-2)" fontWeight={500}>
            → {next_measure_type}
          </text>
        )}

        {/* ── Phase labels inside the track ── */}
        {(() => {
          const epStart = Math.max(0, consumedX);
          const epEnd = phaseX != null ? Math.min(phaseX, todayX) : todayX;
          const epMid = (epStart + epEnd) / 2;
          const etStart = phaseX != null ? phaseX : null;
          const etMid = etStart != null ? (etStart + todayX) / 2 : null;

          return (
            <>
              {epEnd - epStart > 60 && (
                <text x={epMid} y={TRACK_Y + TRACK_H / 2 + 4} textAnchor="middle"
                  fontSize={9} fill="color-mix(in srgb, var(--accent) 80%, var(--ink-1))" fontWeight={600}
                  style={{ pointerEvents: 'none' }}>
                  EP
                </text>
              )}
              {etMid != null && todayX - (etStart ?? 0) > 60 && !phase_planned && (
                <text x={etMid} y={TRACK_Y + TRACK_H / 2 + 4} textAnchor="middle"
                  fontSize={9} fill="color-mix(in srgb, var(--ok) 80%, var(--ink-1))" fontWeight={600}
                  style={{ pointerEvents: 'none' }}>
                  ET
                </text>
              )}
            </>
          );
        })()}

        {/* ── Tooltip ── */}
        {tooltip && (() => {
          const tw = tooltip.text.length * 5.8 + 16;
          const tx = Math.min(W - tw / 2 - 4, Math.max(tw / 2 + 4, tooltip.x));
          return (
            <g style={{ pointerEvents: 'none' }}>
              <rect x={tx - tw / 2} y={tooltip.y - 18} width={tw} height={16} rx={3}
                fill="var(--surface)" stroke="var(--line-strong)" strokeWidth={1} />
              <text x={tx} y={tooltip.y - 7} textAnchor="middle" fontSize={9} fill="var(--ink-2)">
                {tooltip.text}
              </text>
            </g>
          );
        })()}
      </svg>

      {/* ── Legend ── */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 'var(--s12)',
        fontSize: 'var(--t-11)', color: 'var(--ink-3)', marginTop: 'var(--s6)',
      }}>
        <LegendItem color="color-mix(in srgb, var(--accent) 50%, var(--bg))" label={t('timeline.legend.epFull')} />
        <LegendItem color="color-mix(in srgb, var(--ok) 55%, var(--bg))" label={t('timeline.legend.etFull')} />
        {consumed > 0 && <LegendItem color="var(--ink-3)" label={t('timeline.legend.priorInstitution')} opacity={0.3} />}
        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <svg width={16} height={10} viewBox="0 0 16 10">
            <line x1={8} y1={0} x2={8} y2={10} stroke="var(--accent)" strokeWidth={2} />
            <polygon points="4,0 12,0 8,5" fill="var(--accent)" />
          </svg>
          {t('timeline.legend.today')}
        </span>
      </div>
    </div>
  );
}

function LegendItem({ color, label, opacity }: { color: string; label: string; opacity?: number }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <span style={{
        width: 18, height: 8, borderRadius: 2, display: 'inline-block',
        background: color, opacity: opacity ?? 1,
      }} />
      {label}
    </span>
  );
}
